import { useEffect, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, Check, ChevronLeft, ChevronRight, MapPin, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format, getISOWeek } from 'date-fns';
import { EmptyState } from '@/components/Shared/EmptyState';
import { usePlanningAssistantStore, type PlanningAssistantMode } from '@/store/usePlanningAssistantStore';
import { addIsoDays, getDateLocale, isoDate, parseIsoDate } from '@/utils/dateUtils';
import { asDueCategory, fmtHours, isAvailable } from './assistantFormat';
import { EmployeeMode } from './EmployeeMode';
import { MapMode } from './MapMode';
import { TaskMode } from './TaskMode';
import { UndoToast } from './components/UndoToast';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const MODES: { key: PlanningAssistantMode; icon: typeof Users }[] = [
  { key: 'employee', icon: Users },
  { key: 'task', icon: Check },
  { key: 'map', icon: MapPin },
];

function Kpi({ value, label, tone = 'text-slate-900', extra }: { value: string | number; label: string; tone?: string; extra?: ReactNode }) {
  return (
    <span>
      <span className={`font-semibold font-mono tabular-nums ${tone}`}>{value}</span> {label}
      {extra}
    </span>
  );
}

export default function PlanningAssistantPage() {
  const { t, i18n } = useTranslation('planning');
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('date');
  const date = param && ISO_DAY.test(param) ? param : isoDate(new Date());

  const mode = usePlanningAssistantStore((s) => s.mode);
  const setMode = usePlanningAssistantStore((s) => s.setMode);
  const setDate = usePlanningAssistantStore((s) => s.setDate);
  const fetchDay = usePlanningAssistantStore((s) => s.fetchDay);
  const data = usePlanningAssistantStore((s) => s.data);
  const loading = usePlanningAssistantStore((s) => s.loading);
  const error = usePlanningAssistantStore((s) => s.error);
  const newlyAssigned = usePlanningAssistantStore((s) => s.newlyAssignedTaskIds.length);
  // why: assign/undo finish against the day they started on — no day switch meanwhile.
  const busy = usePlanningAssistantStore((s) => s.busy);

  useEffect(() => {
    setDate(date);
  }, [date, setDate]);

  const stepDay = (days: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('date', addIsoDays(date, days));
    setSearchParams(next);
  };

  const day = parseIsoDate(date);
  const dateLabel = `${format(day, t('assistant.toolbar.datePattern'), { locale: getDateLocale() })} · ${t(
    'assistant.toolbar.calendarWeek',
    { week: getISOWeek(day) },
  )}`;

  const tasks = data?.tasks ?? [];
  const openCount = tasks.length;
  const todayDue = tasks.filter((x) => asDueCategory(x.dueCategory) === 'Day').length;
  const freeMinutes = (data?.employees ?? [])
    .filter(isAvailable)
    .reduce((sum, e) => sum + Math.max(0, e.freeMinutes ?? 0), 0);

  const kpis = data ? (
    <>
      <Kpi
        value={openCount}
        label={t('assistant.kpis.open')}
        extra={todayDue > 0 ? <span className="text-red-600"> · {t('assistant.kpis.todayDue', { count: todayDue })}</span> : null}
      />
      <Kpi value={fmtHours(freeMinutes, i18n.language)} label={t('assistant.kpis.freeCapacity')} />
      {newlyAssigned > 0 && (
        <Kpi value={newlyAssigned} label={t('assistant.kpis.newlyAssigned')} tone="text-emerald-700" />
      )}
    </>
  ) : null;

  return (
    <div className="flex flex-col flex-1 min-h-0 -mx-6 -mt-6">
      {/* Toolbar */}
      <div className="h-14 border-b border-slate-200 bg-white px-4 lg:px-6 flex items-center gap-3 lg:gap-4 shrink-0 whitespace-nowrap overflow-x-auto">
        <h1 className="text-[15px] font-semibold text-slate-900 shrink-0">{t('assistant.toolbar.title')}</h1>
        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-medium border border-emerald-200 hidden md:inline">
          {t('assistant.toolbar.badge')}
        </span>
        <div className="flex items-center gap-1 text-[12px] text-slate-500 shrink-0">
          <button
            type="button"
            onClick={() => stepDay(-1)}
            disabled={busy}
            aria-label={t('assistant.toolbar.prevDay')}
            className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none"
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </button>
          <span className="tabular-nums text-slate-700 font-medium">{dateLabel}</span>
          <button
            type="button"
            onClick={() => stepDay(1)}
            disabled={busy}
            aria-label={t('assistant.toolbar.nextDay')}
            className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none"
          >
            <ChevronRight size={14} strokeWidth={2} />
          </button>
        </div>
        <div className="flex p-0.5 rounded-lg bg-slate-100 ml-1 shrink-0" role="tablist">
          {MODES.map(({ key, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={mode === key}
              onClick={() => setMode(key)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[12px] ${
                mode === key ? 'bg-white text-slate-900 font-medium shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Icon size={13} strokeWidth={2} />
              {t(`assistant.modes.${key}`)}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="hidden 2xl:flex items-center gap-5 text-[12px] text-slate-500">{kpis}</div>
        <Link
          to="/planning"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-[12px] text-slate-600 hover:bg-slate-50 shrink-0"
        >
          <Calendar size={14} strokeWidth={2} />
          <span className="hidden lg:inline">{t('assistant.toolbar.weekPlan')}</span>
        </Link>
      </div>
      {kpis && (
        <div className="flex 2xl:hidden items-center gap-5 px-4 lg:px-6 py-2 border-b border-slate-200 bg-white text-[12px] text-slate-500 shrink-0 whitespace-nowrap overflow-x-auto">
          {kpis}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 p-4 lg:p-5 overflow-y-auto">
        {error ? (
          <div className="bg-white border border-slate-200 rounded-xl">
            <EmptyState variant="error" title={t(error)} onRetry={() => void fetchDay(date)} />
          </div>
        ) : loading || !data ? (
          <div className="grid gap-4 lg:h-full grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(340px,420px)] animate-pulse" aria-hidden>
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-4 min-h-[420px]">
              <div className="h-10 w-1/2 rounded-md bg-slate-100" />
              <div className="h-[52px] rounded-md bg-slate-100" />
              <div className="flex-1 rounded-lg bg-slate-100" />
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col gap-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-28 rounded-lg bg-slate-100" />
              ))}
            </div>
          </div>
        ) : mode === 'employee' ? (
          <EmployeeMode data={data} />
        ) : mode === 'task' ? (
          <TaskMode data={data} />
        ) : (
          <MapMode data={data} />
        )}
      </div>

      <UndoToast />
    </div>
  );
}
